import { useCallback, useEffect } from 'react';
import { useStore } from 'hooks/useStore';
import { OOCStatus, OOCSubmission, OOCTabType, OOC_DATE_FILTER_OPTIONS } from '../oocUpdated.type';
import { isCurrentUser } from 'utils/userUtils';

export const CONTRACT_TYPE_OPTIONS: { label: string; value: OOCTabType | 'all' }[] = [
  { label: 'All Contract Types', value: 'all' },
  { label: 'MAS DP', value: 'MAS' },
  { label: 'NC DP', value: 'NC' },
  { label: 'Prov. MAS', value: 'ProvMAS' },
  { label: 'Prov. NC', value: 'ProvNC' },
];

export const DATE_DROPDOWN_OPTIONS = OOC_DATE_FILTER_OPTIONS.map((d) => ({
  label: d,
  value: d,
}));

export type SummaryCardConfig = {
  key: OOCStatus | 'all' | 'history';
  label: string;
  count: number;
  iconType: 'fileText' | 'mail' | 'clock';
  colorClass: 'blue' | 'gray';
};

export const getStatusBadgeClass = (status: OOCStatus, styles: Record<string, string>): string => {
  switch (status) {
    case 'draft':
      return styles.draft;
    case 'communicated':
      return styles.communicated;
    case 'integrated':
      return styles.integrated;
    default:
      return '';
  }
};

export const getStatusLabel = (status: OOCStatus): string => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'communicated':
      return 'Submitted / Communicated';
    case 'integrated':
      return 'Integrated';
    default:
      return status;
  }
};

const _currentPlanningPeriod = (): string => {
  const now = new Date();
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[now.getMonth()]} ${now.getFullYear()}`;
};

export const useOOCDashboardLogic = () => {
  const { OOCStoreUpdated: OOCStore, UserStore } = useStore();
  const loggedInUserName = UserStore.userMetadata?.userInfo?.name ?? '';

  useEffect(() => {
    OOCStore.fetchSubmissions();
  }, [OOCStore]);

  const handleCardClick = useCallback(
    (filter: OOCStatus | 'all' | 'history') => {
      if (filter === 'history') {
        OOCStore.setScreen('history');
        return;
      }
      OOCStore.setActiveCardFilter(filter);
    },
    [OOCStore]
  );

  const handleCreateNew = useCallback(() => {
    OOCStore.createNewSubmission(loggedInUserName, _currentPlanningPeriod());
  }, [OOCStore, loggedInUserName]);

  // Drafts are scoped to the logged-in user; submitted/communicated are visible to everyone.
  const displayedSubmissions: OOCSubmission[] = OOCStore.filteredSubmissions.filter(
    (sub) => sub.status !== 'draft' || isCurrentUser(loggedInUserName, sub.submittedBy)
  );

  const myDraftCount = OOCStore.submissions.filter(
    (sub) => sub.status === 'draft' && isCurrentUser(loggedInUserName, sub.submittedBy)
  ).length;

  const summaryCardConfigs: SummaryCardConfig[] = [
    {
      key: 'all',
      label: 'All Active',
      count: OOCStore.dashboardCounts.all,
      iconType: 'fileText',
      colorClass: 'blue',
    },
    {
      key: 'draft',
      label: 'Drafts',
      count: myDraftCount,
      iconType: 'fileText',
      colorClass: 'blue',
    },
    {
      key: 'communicated',
      label: 'Submitted / Communicated',
      count: OOCStore.dashboardCounts.communicated,
      iconType: 'mail',
      colorClass: 'blue',
    },
    {
      key: 'history',
      label: 'History',
      count: OOCStore.dashboardCounts.history,
      iconType: 'clock',
      colorClass: 'gray',
    },
  ];

  return {
    OOCStore,
    displayedSubmissions,
    loggedInUserName,
    handleCardClick,
    handleCreateNew,
    summaryCardConfigs,
  };
};
